/**
 * InlineAiSuggestion — shown beneath an edit field (Feature 035 / 036).
 *
 * Feature 036 additions:
 * - When `onLocate` is provided, the suggestion value becomes a clickable button
 *   that calls `onLocate()` to switch the document viewer to the source region.
 *   No icon — just the text as a link-styled button.
 * - When `onLocate` is absent, the value renders as plain text (existing behavior).
 * - Accept applies the suggestion and swaps the accept control for an undo
 *   control; undo restores the field to its pre-accept value and brings the
 *   accept control back.
 * - No separate dismiss control: an unaccepted suggestion already leaves the
 *   field unchanged, so accept/undo is the complete interaction (product
 *   decision, spec.md FR-006/FR-017).
 * - `locked` disables accept/undo (they mutate form data) but never the locate
 *   control — locate only navigates the document viewer, so it stays usable
 *   even when the encounter/claim is locked from editing.
 */

import { memo, useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';

interface InlineAiSuggestionProps {
  suggestion: string;
  currentValue: string;
  onAccept: (value: string) => void;
  /** When provided, renders the suggestion value as a clickable locate target. */
  onLocate?: () => void;
  /**
   * Optional formatted text shown in place of the raw suggestion (e.g. a date
   * rendered MM/DD/YYYY). Accept and the mismatch check still use the raw
   * `suggestion`, so the value written to the field is unchanged.
   */
  displayValue?: string;
  /** Disables accept/undo (data-mutating) while leaving locate usable. */
  locked?: boolean;
}

/**
 * Ignores punctuation (e.g. address commas) and whitespace-run differences between words, but
 * keeps a single space between words significant so distinct words are never fused together.
 */
function norm(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function InlineAiSuggestion({ suggestion, currentValue, onAccept, onLocate, displayValue, locked }: InlineAiSuggestionProps) {
  const [previousValue, setPreviousValue] = useState<string | null>(null);
  if (!suggestion) return null;

  const accepted = previousValue !== null;
  const mismatch = !accepted && currentValue.trim() !== '' && norm(currentValue) !== norm(suggestion);
  const shown = displayValue ?? suggestion;

  return (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', gap: 0.75, mt: 0.5 }}
      role="group"
      aria-label="AI suggestion"
    >
      <AutoAwesomeIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
      {onLocate ? (
        <Box
          component="button"
          type="button"
          aria-label={`Locate ${shown}`}
          onClick={onLocate}
          sx={{
            fontSize: 12,
            color: 'text.secondary',
            flex: 1,
            minWidth: 0,
            textAlign: 'left',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: 'none',
            '&:hover': { color: 'text.primary' },
          }}
        >
          {shown}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', flex: 1, minWidth: 0 }} noWrap>
          {shown}
        </Typography>
      )}
      {mismatch && (
        <Box
          component="span"
          title="AI-extracted value differs from the original"
          sx={{
            fontSize: 10,
            fontWeight: 600,
            color: 'colors.mismatchText',
            bgcolor: 'colors.mismatchBg',
            border: '1px solid',
            borderColor: 'colors.mismatchBorder',
            borderRadius: '9999px',
            padding: '0px 6px',
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            cursor: 'default',
            flexShrink: 0,
          }}
        >
          Mismatch
        </Box>
      )}
      {accepted ? (
        <Tooltip title="Undo AI suggestion">
          <IconButton
            size="small"
            aria-label="Undo AI suggestion"
            disabled={locked}
            onClick={() => {
              onAccept(previousValue);
              setPreviousValue(null);
            }}
            sx={{ p: 0.25, color: 'primary.main' }}
          >
            <ReplayIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <IconButton
          size="small"
          aria-label="Accept AI suggestion"
          disabled={locked}
          onClick={() => {
            setPreviousValue(currentValue);
            onAccept(suggestion);
          }}
          sx={{ p: 0.25, color: 'primary.main' }}
        >
          <CheckIcon sx={{ fontSize: 15 }} />
        </IconButton>
      )}
    </Stack>
  );
}

export default memo(InlineAiSuggestion);
